import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import AddIcon from '@/components/icons/AddIcon';
import MinusIcon from '@/components/icons/MinusIcon';
import NewOrderServiceSkeleton from '@/features/orders/components/NewOrder/NewOrderServices/NewOrderServiceSkeleton';
import { useOrderItemsListApi } from '@/features/orders/hooks/useOrderItemsListApi';
import EImageWithFallback from '@/components/everridge/EImageWithFallback';
import { Control, Controller, useWatch } from 'react-hook-form';
import type { NewOrderFormData } from '@/features/orders/validators/NewOrderValidator';
import { FC } from 'react';
import { GetOrderItemsListItem } from '@/features/orders/types/orders-dto';
import { NumbersHelper } from '@/utils/numbersHelper';
import { cn } from '@/lib/utils';

type Props = {
	control: Control<NewOrderFormData>;
	isMobile?: boolean;
};

const NewOrderService: FC<Props> = ({ control, isMobile }) => {
	const { t } = useTranslation();

	const serviceId = useWatch({ control, name: 'serviceId' })?.[0]?.id;

	const { orderItemsListData, isLoadingOrderItemsListData } =
		useOrderItemsListApi({ serviceId });

	return (
		<>
			{isLoadingOrderItemsListData &&
				Array.from({ length: 5 }).map((_, index) => (
					<NewOrderServiceSkeleton key={index} />
				))}
			{!isLoadingOrderItemsListData &&
				!!orderItemsListData?.items?.length && (
					<Controller
						name={'lines'}
						control={control}
						render={({ field: { value = [], onChange } }) => {
							const getQuantity = (id: string) =>
								value.find((v) => v.orderItemId === id)
									?.quantity;

							const handleIncrement = ({
								id,
								price,
							}: GetOrderItemsListItem) => {
								const index = value.findIndex(
									(item) => item.orderItemId === id,
								);

								if (index === -1) {
									onChange([
										...value,
										{ orderItemId: id, quantity: 1, price },
									]);
									return;
								}

								const updated = [...value];

								updated[index] = {
									...updated[index],
									quantity: updated[index].quantity + 1,
								};

								onChange(updated);
							};

							const handleDecrement = ({
								id,
							}: GetOrderItemsListItem) => {
								const index = value.findIndex(
									(item) => item.orderItemId === id,
								);

								if (index === -1) return;

								const existingItem = value[index];

								const updated = [...value];

								updated[index] = {
									...existingItem,
									quantity: existingItem.quantity - 1,
								};

								onChange(updated);
							};

							return (
								<>
									{orderItemsListData?.items?.map((item) => {
										const quantity = getQuantity(item.id);

										return (
											<div
												key={item.id}
												className='flex items-center select-none'>
												<div
													className={`flex items-center gap-4
															${!quantity ? 'animate-fadeOut' : 'animate-fadeIn mr-4'}
														`}
													style={{
														display:
															quantity ===
															undefined
																? 'none'
																: '',
													}}>
													<Button
														variant='outline'
														size='sm'
														onClick={() =>
															handleDecrement(
																item,
															)
														}
														className='shrink-0'
														disabled={
															quantity ===
																undefined ||
															quantity <= 0
														}>
														<MinusIcon />
													</Button>
													<span className='text-base font-medium'>{`${quantity}X`}</span>
												</div>
												<div className='flex items-center gap-4 truncate mr-4'>
													<EImageWithFallback
														src={item.imageUrl}
														containerClassName='size-11 rounded-semi-xl overflow-hidden'
													/>
													<div className='flex flex-col truncate'>
														<h5
															className={cn(
																'text-base font-medium truncate',
																isMobile &&
																	'text-sm',
															)}>
															{item.name ?? ''}
														</h5>
														<span
															className={`text-sm ${
																item.price
																	? ''
																	: 'text-success-1000'
															}`}>
															{NumbersHelper.formatValueWithoutStyling(
																item.price,
															)}
														</span>
													</div>
												</div>
												<Button
													variant='outline'
													size='sm'
													className='ml-auto'
													onClick={() =>
														handleIncrement(item)
													}
													disabled={
														(quantity ?? 0) >= 5
													}>
													<AddIcon />
												</Button>
											</div>
										);
									})}
								</>
							);
						}}
					/>
				)}
			{!isLoadingOrderItemsListData &&
				!orderItemsListData?.items?.length && (
					<span className='text-sm text-popover-800'>
						{t(
							'main.orders.components.create.components.services.noItems',
						)}
					</span>
				)}
		</>
	);
};

export default NewOrderService;
